import { isIncome, isPayment, participantName } from "./expense";
import { spentAtOf } from "./spent-at";

const MAX_NAMES = 3;

const receiverIdsOf = (expense) => {
  const receivers = expense.receivers || [];
  const withShare = receivers.filter((r) => r.absolute_share_in_decimals > 0);
  return (withShare.length ? withShare : receivers).map(
    (r) => r.participant_id,
  );
};

const listNames = (names) => {
  if (names.length <= MAX_NAMES) return names.join(", ");
  const shown = names.slice(0, MAX_NAMES - 1);
  return `${shown.join(", ")} und ${names.length - shown.length} weitere`;
};

export const describePayment = (expense, participants = []) => {
  const [recipient] = receiverIdsOf(expense);
  return `${participantName(participants, expense.payer)} → ${participantName(
    participants,
    recipient,
  )}`;
};

const beneficiariesOf = (expense, participants) => {
  const ids = [...new Set(receiverIdsOf(expense))];
  if (ids.length === 0) return "";
  const isAll =
    participants.length > 1 &&
    ids.length === participants.length &&
    participants.every((participant) => ids.includes(participant.id));
  if (isAll) return "alle";
  const ordered = [
    ...participants.filter((p) => ids.includes(p.id)).map((p) => p.id),
    ...ids.filter((id) => !participants.some((p) => p.id === id)),
  ];
  return listNames([
    ...(ordered.includes(expense.payer) ? ["sich"] : []),
    ...ordered
      .filter((id) => id !== expense.payer)
      .map((id) => participantName(participants, id)),
  ]);
};

export const describeReceivers = (expense, participants = []) => {
  if (isPayment(expense)) return describePayment(expense, participants);
  const payer = participantName(participants, expense.payer);
  const beneficiaries = beneficiariesOf(expense, participants);
  if (isIncome(expense)) {
    const received = `${payer} hat erhalten`;
    return beneficiaries ? `${received} · für ${beneficiaries}` : received;
  }
  return beneficiaries ? `${payer} für ${beneficiaries}` : payer;
};

const isoDateFormats = new Map();

const isoDateFormat = (timeZone) => {
  if (!isoDateFormats.has(timeZone)) {
    isoDateFormats.set(
      timeZone,
      new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    );
  }
  return isoDateFormats.get(timeZone);
};

export const dayKey = (date, timeZone = undefined) =>
  isoDateFormat(timeZone).format(new Date(date));

const previousDayKey = (key) => {
  const date = new Date(`${key}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};

const labelFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
  month: "short",
});
const labelFormatWithYear = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

export const dayLabel = (key, now = new Date(), timeZone = undefined) => {
  const today = dayKey(now, timeZone);
  if (key === today) return "Heute";
  if (key === previousDayKey(today)) return "Gestern";
  // Noon UTC of the local calendar day: formatting in UTC keeps that day.
  const date = new Date(`${key}T12:00:00Z`);
  return (key.slice(0, 4) === today.slice(0, 4)
    ? labelFormat
    : labelFormatWithYear
  ).format(date);
};

const timeOf = (expense) => {
  const time = Date.parse(spentAtOf(expense));
  return Number.isNaN(time) ? 0 : time;
};

// spent_at (old cached data: created_at), not updated_at: editing must not reorder.
export const sortExpenses = (expenses = []) =>
  [...expenses].sort(
    (a, b) => timeOf(b) - timeOf(a) || Number(b.id) - Number(a.id),
  );

export const groupExpensesByDay = (
  expenses = [],
  now = new Date(),
  timeZone = undefined,
) => {
  const days = [];
  for (const expense of sortExpenses(expenses)) {
    const key = dayKey(timeOf(expense), timeZone);
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, label: dayLabel(key, now, timeZone), expenses: [] };
      days.push(day);
    }
    day.expenses.push(expense);
  }
  return days;
};

export const UNTAGGED = "untagged";

const tagIdsOf = (expense) => (expense.tags ?? []).map((tag) => `${tag.id}`);

export const filterByTags = (expenses = [], filter = []) => {
  if (filter.length === 0) return expenses;
  const wanted = new Set(filter.map(String));
  return expenses.filter((expense) => {
    const ids = tagIdsOf(expense);
    if (ids.length === 0) return wanted.has(UNTAGGED);
    return ids.some((id) => wanted.has(id));
  });
};
